// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, X, GripVertical } from 'lucide-react'
import { Badge, Button, Input, Textarea, Select, Toggle } from '@/components/ui'
import { FileUpload } from '@/components/ui/FileUpload'
import { api } from '@/lib/api'
import type {
  MicrositePage,
  MicrositePageType,
  MicrositePageContent,
  HomePageContent,
  HomeRoomItem,
  RoomPageContent,
  ContactPageContent,
  ButtonAction,
  ButtonActionType,
} from '@/hooks/useMicrosite'

const ACTION_TYPES: ButtonActionType[] = ['link', 'page', 'phone', 'email', 'line']

function slugifyTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

async function uploadImage(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(presigned.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } })
  return `/api/images/${presigned.key}`
}

interface FormState {
  title: string
  slug: string
  page_type: MicrositePageType
  content: MicrositePageContent
  is_published: boolean
  show_in_nav: boolean
}

export interface MicrositePageEditorProps {
  page?: MicrositePage | null
  defaultPageType?: MicrositePageType
  onSave: (data: FormState) => Promise<void>
  onCancel: () => void
  isSaving: boolean
  onContentChange?: (content: MicrositePageContent) => void
  onPageTypeChange?: (pageType: MicrositePageType) => void
}

function defaultContent(pageType: MicrositePageType): MicrositePageContent {
  switch (pageType) {
    case 'home':
      return {
        hero_title: '', hero_subtitle: '',
        hero_cta: { text: '', action_type: 'page', slug: '' },
        rooms: [],
      }
    case 'room':
      return {
        room_name: '', price: '', size_sqm: '', description: '',
        amenities: [], images: [],
        cta: { text: '', action_type: 'page', slug: '' },
        available_rooms: [],
      }
    case 'contact':
      return {
        heading: '', description: '',
        phone: '', email: '', address: '',
        google_maps_embed_url: '',
        cta: { text: '', action_type: 'email', email: '' },
      }
    default:
      return {}
  }
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-xs font-semibold text-base-content/50 uppercase tracking-wider pt-2">{children}</h4>
  )
}

/** Reusable button action editor */
function ButtonActionEditor({
  action,
  onChange,
  t,
}: {
  action: ButtonAction
  onChange: (action: ButtonAction) => void
  t: (key: string) => string
}) {
  return (
    <div className="bg-base-200/50 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input
          label={t('pages.button.text')}
          placeholder={t('pages.button.text_placeholder')}
          value={action.text}
          onChange={(e) => onChange({ ...action, text: e.target.value })}
        />
        <Select
          label={t('pages.button.action_type')}
          options={ACTION_TYPES.map((at) => ({ value: at, label: t(`pages.button.types.${at}`) }))}
          value={action.action_type}
          onChange={(e) => {
            const at = e.target.value as ButtonActionType
            onChange({ text: action.text, action_type: at })
          }}
        />
      </div>

      {/* Action-type-specific fields */}
      {action.action_type === 'link' && (
        <Input
          label={t('pages.button.url')}
          placeholder="https://..."
          value={action.url ?? ''}
          onChange={(e) => onChange({ ...action, url: e.target.value })}
        />
      )}
      {action.action_type === 'page' && (
        <Input
          label={t('pages.button.page_slug')}
          placeholder={t('pages.button.page_slug_placeholder')}
          value={action.slug ?? ''}
          onChange={(e) => onChange({ ...action, slug: e.target.value })}
        />
      )}
      {action.action_type === 'phone' && (
        <Input
          label={t('pages.button.phone')}
          placeholder="02-xxx-xxxx"
          value={action.phone ?? ''}
          onChange={(e) => onChange({ ...action, phone: e.target.value })}
        />
      )}
      {action.action_type === 'email' && (
        <Input
          label={t('pages.button.email')}
          placeholder="contact@example.com"
          value={action.email ?? ''}
          onChange={(e) => onChange({ ...action, email: e.target.value })}
        />
      )}
      {action.action_type === 'line' && (
        <Input
          label={t('pages.button.line_id')}
          placeholder="@yourlineid"
          value={action.line_id ?? ''}
          onChange={(e) => onChange({ ...action, line_id: e.target.value })}
        />
      )}
    </div>
  )
}

export function MicrositePageEditor({
  page,
  onSave,
  onCancel,
  isSaving,
  onContentChange,
  defaultPageType = 'room',
}: MicrositePageEditorProps) {
  const { t } = useTranslation('microsite')
  const { t: tc } = useTranslation('common')

  const initType = page?.page_type ?? defaultPageType
  const [form, setForm] = useState<FormState>({
    title: page?.title ?? '',
    slug: page?.slug ?? '',
    page_type: initType,
    content: page?.content && Object.keys(page.content).length > 0
      ? page.content
      : defaultContent(initType),
    is_published: page?.is_published ?? false,
    show_in_nav: page?.show_in_nav ?? true,
  })
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({})
  const amenityInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const pt = page?.page_type ?? defaultPageType
    const content = page?.content && Object.keys(page.content).length > 0
      ? page.content
      : defaultContent(pt)
    queueMicrotask(() => {
      setForm({
        title: page?.title ?? '',
        slug: page?.slug ?? '',
        page_type: pt,
        content,
        is_published: page?.is_published ?? false,
        show_in_nav: page?.show_in_nav ?? true,
      })
      setErrors({})
    })
  }, [page, defaultPageType])

  const updateContent = useCallback((key: string, value: unknown) => {
    setForm((prev) => {
      const next = { ...prev, content: { ...prev.content, [key]: value } }
      onContentChange?.(next.content)
      return next
    })
  }, [onContentChange])

  const handleTitleChange = useCallback((title: string) => {
    setForm((prev) => ({
      ...prev,
      title,
      slug: page ? prev.slug : slugifyTitle(title),
    }))
    if (errors.title) setErrors((e) => ({ ...e, title: undefined }))
  }, [page, errors.title])

  const validate = (): boolean => {
    const next: Partial<Record<string, string>> = {}
    if (!form.title.trim()) next.title = t('pages.fields.title') + ' is required'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    await onSave(form)
  }

  const homeContent = form.content as HomePageContent
  const roomContent = form.content as RoomPageContent
  const contactContent = form.content as ContactPageContent

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      {/* Page Settings */}
      <Input
        label={t('pages.fields.title')}
        placeholder={t('pages.fields.title_placeholder')}
        value={form.title}
        onChange={(e) => handleTitleChange(e.target.value)}
        error={errors.title}
        required
      />

      <div className="divider my-1" />

      {/* === HOME FIELDS === */}
      {form.page_type === 'home' && (
        <div className="space-y-4">
          <SectionLabel>{t('pages.home.section_hero')}</SectionLabel>
          <Input
            label={t('pages.home.hero_title')}
            placeholder={t('pages.home.hero_title_placeholder')}
            value={homeContent.hero_title ?? ''}
            onChange={(e) => updateContent('hero_title', e.target.value)}
          />
          <Input
            label={t('pages.home.hero_subtitle')}
            placeholder={t('pages.home.hero_subtitle_placeholder')}
            value={homeContent.hero_subtitle ?? ''}
            onChange={(e) => updateContent('hero_subtitle', e.target.value)}
          />

          <SectionLabel>{t('pages.button.section')}</SectionLabel>
          <ButtonActionEditor
            action={homeContent.hero_cta ?? { text: '', action_type: 'page' }}
            onChange={(cta) => updateContent('hero_cta', cta)}
            t={t}
          />

          {/* Rooms list */}
          <SectionLabel>{t('pages.home.rooms_title')}</SectionLabel>
          <div className="space-y-3">
            {(homeContent.rooms ?? []).map((room, idx) => (
              <div key={idx} className="bg-base-200/50 rounded-lg p-4 space-y-3 relative group">
                <div className="absolute top-3 right-3 flex items-center gap-1">
                  <span className="text-base-content/30 cursor-grab"><GripVertical size={14} /></span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    shape="square"
                    className="text-base-content/40 hover:text-error"
                    aria-label={tc('actions.remove')}
                    onClick={() => {
                      const next = [...(homeContent.rooms ?? [])]
                      next.splice(idx, 1)
                      updateContent('rooms', next)
                    }}
                  >
                    <X size={14} />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label={t('pages.home.room_name')}
                    placeholder={t('pages.home.room_name_placeholder')}
                    value={room.name}
                    onChange={(e) => {
                      const next = [...(homeContent.rooms ?? [])]
                      next[idx] = { ...next[idx], name: e.target.value }
                      updateContent('rooms', next)
                    }}
                  />
                  <Input
                    label={t('pages.home.room_price')}
                    placeholder="8,000"
                    value={room.price}
                    onChange={(e) => {
                      const next = [...(homeContent.rooms ?? [])]
                      next[idx] = { ...next[idx], price: e.target.value }
                      updateContent('rooms', next)
                    }}
                  />
                  <Input
                    label={t('pages.home.room_size')}
                    placeholder="35"
                    value={room.size_sqm}
                    onChange={(e) => {
                      const next = [...(homeContent.rooms ?? [])]
                      next[idx] = { ...next[idx], size_sqm: e.target.value }
                      updateContent('rooms', next)
                    }}
                  />
                </div>
                <FileUpload
                  label={t('pages.home.room_image')}
                  value={room.image ? [room.image] : []}
                  onChange={(urls) => {
                    const next = [...(homeContent.rooms ?? [])]
                    next[idx] = { ...next[idx], image: urls[0] ?? '' }
                    updateContent('rooms', next)
                  }}
                  maxFiles={1}
                  accept="image/*"
                  onUpload={uploadImage}
                />
                <ButtonActionEditor
                  action={room.cta ?? { text: '', action_type: 'page', slug: '' }}
                  onChange={(cta) => {
                    const next = [...(homeContent.rooms ?? [])]
                    next[idx] = { ...next[idx], cta }
                    updateContent('rooms', next)
                  }}
                  t={t}
                />
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={<Plus size={14} />}
              onClick={() => {
                const next: HomeRoomItem[] = [...(homeContent.rooms ?? []), { name: '', price: '', size_sqm: '' }]
                updateContent('rooms', next)
              }}
            >
              {t('pages.home.add_room')}
            </Button>
          </div>
        </div>
      )}

      {/* === ROOM FIELDS === */}
      {form.page_type === 'room' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Input
              label={t('pages.room.room_name')}
              placeholder={t('pages.room.room_name_placeholder')}
              value={roomContent.room_name ?? ''}
              onChange={(e) => updateContent('room_name', e.target.value)}
            />
            <Input
              label={t('pages.room.price')}
              placeholder="8,000"
              value={roomContent.price ?? ''}
              onChange={(e) => updateContent('price', e.target.value)}
            />
            <Input
              label={t('pages.room.size_sqm')}
              placeholder="35"
              value={roomContent.size_sqm ?? ''}
              onChange={(e) => updateContent('size_sqm', e.target.value)}
            />
          </div>

          <SectionLabel>{t('pages.room.description')}</SectionLabel>
          <Textarea
            placeholder={t('pages.room.description_placeholder')}
            rows={3}
            value={roomContent.description ?? ''}
            onChange={(e) => updateContent('description', e.target.value)}
            className="text-sm"
          />

          {/* Amenities */}
          <SectionLabel>{t('pages.room.amenities')}</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {(roomContent.amenities ?? []).map((amenity, idx) => (
              <Badge key={idx} size="lg" className="gap-1.5 bg-base-200">
                {amenity}
                <button
                  type="button"
                  className="text-base-content/40 hover:text-error transition-colors"
                  aria-label={tc('actions.remove')}
                  onClick={() => {
                    const next = [...(roomContent.amenities ?? [])]
                    next.splice(idx, 1)
                    updateContent('amenities', next)
                  }}
                >
                  <X size={12} />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              ref={amenityInputRef}
              type="text"
              size="sm"
              className="flex-1"
              placeholder={t('pages.room.amenity_placeholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  const val = amenityInputRef.current?.value.trim() ?? ''
                  if (val) {
                    updateContent('amenities', [...(roomContent.amenities ?? []), val])
                    if (amenityInputRef.current) amenityInputRef.current.value = ''
                  }
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              shape="square"
              aria-label={t('pages.room.add_amenity')}
              onClick={() => {
                const val = amenityInputRef.current?.value.trim() ?? ''
                if (val) {
                  updateContent('amenities', [...(roomContent.amenities ?? []), val])
                  if (amenityInputRef.current) amenityInputRef.current.value = ''
                }
              }}
            >
              <Plus size={14} />
            </Button>
          </div>

          {/* Images */}
          <SectionLabel>{t('pages.room.images')}</SectionLabel>
          <FileUpload
            hint={t('pages.room.images_hint')}
            value={roomContent.images ?? []}
            onChange={(urls) => updateContent('images', urls)}
            maxFiles={10}
            accept="image/*"
            onUpload={uploadImage}
          />

          {/* CTA Button */}
          <SectionLabel>{t('pages.button.section')}</SectionLabel>
          <ButtonActionEditor
            action={roomContent.cta ?? { text: '', action_type: 'page' }}
            onChange={(cta) => updateContent('cta', cta)}
            t={t}
          />

          {/* Available Rooms */}
          <SectionLabel>{t('pages.room.available_rooms')}</SectionLabel>
          <div className="space-y-3">
            {(roomContent.available_rooms ?? []).map((room, idx) => (
              <div key={idx} className="bg-base-200/50 rounded-lg p-4 space-y-3 relative group">
                <div className="absolute top-3 right-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    shape="square"
                    className="text-base-content/40 hover:text-error"
                    aria-label={tc('actions.remove')}
                    onClick={() => {
                      const next = [...(roomContent.available_rooms ?? [])]
                      next.splice(idx, 1)
                      updateContent('available_rooms', next)
                    }}
                  >
                    <X size={14} />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label={t('pages.home.room_name')}
                    placeholder={t('pages.home.room_name_placeholder')}
                    value={room.name}
                    onChange={(e) => {
                      const next = [...(roomContent.available_rooms ?? [])]
                      next[idx] = { ...next[idx], name: e.target.value }
                      updateContent('available_rooms', next)
                    }}
                  />
                  <Input
                    label={t('pages.home.room_price')}
                    placeholder="8,000"
                    value={room.price}
                    onChange={(e) => {
                      const next = [...(roomContent.available_rooms ?? [])]
                      next[idx] = { ...next[idx], price: e.target.value }
                      updateContent('available_rooms', next)
                    }}
                  />
                  <Input
                    label={t('pages.home.room_size')}
                    placeholder="35"
                    value={room.size_sqm}
                    onChange={(e) => {
                      const next = [...(roomContent.available_rooms ?? [])]
                      next[idx] = { ...next[idx], size_sqm: e.target.value }
                      updateContent('available_rooms', next)
                    }}
                  />
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={<Plus size={14} />}
              onClick={() => {
                const next: HomeRoomItem[] = [...(roomContent.available_rooms ?? []), { name: '', price: '', size_sqm: '' }]
                updateContent('available_rooms', next)
              }}
            >
              {t('pages.room.add_available_room')}
            </Button>
          </div>
        </div>
      )}

      {/* === CONTACT FIELDS === */}
      {form.page_type === 'contact' && (
        <div className="space-y-4">
          <Input
            label={t('pages.contact.heading')}
            placeholder={t('pages.contact.heading_placeholder')}
            value={contactContent.heading ?? ''}
            onChange={(e) => updateContent('heading', e.target.value)}
          />
          <div className="form-control w-full">
            <label className="label">
              <span className="label-text">{t('pages.contact.description')}</span>
            </label>
            <Textarea
              placeholder={t('pages.contact.description_placeholder')}
              rows={3}
              value={contactContent.description ?? ''}
              onChange={(e) => updateContent('description', e.target.value)}
              className="text-sm"
            />
          </div>

          <SectionLabel>{t('pages.contact.contact_info')}</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={t('pages.contact.phone')}
              placeholder={t('pages.contact.phone_placeholder')}
              value={contactContent.phone ?? ''}
              onChange={(e) => updateContent('phone', e.target.value)}
            />
            <Input
              label={t('pages.contact.email')}
              placeholder={t('pages.contact.email_placeholder')}
              type="email"
              value={contactContent.email ?? ''}
              onChange={(e) => updateContent('email', e.target.value)}
            />
          </div>
          <Textarea
            label={t('pages.contact.address')}
            placeholder={t('pages.contact.address_placeholder')}
            rows={2}
            value={contactContent.address ?? ''}
            onChange={(e) => updateContent('address', e.target.value)}
            className="text-sm"
          />

          <Input
            label={t('pages.contact.google_maps_url')}
            placeholder={t('pages.contact.google_maps_url_placeholder')}
            value={contactContent.google_maps_embed_url ?? ''}
            onChange={(e) => updateContent('google_maps_embed_url', e.target.value)}
          />

          {/* CTA Button */}
          <SectionLabel>{t('pages.button.section')}</SectionLabel>
          <ButtonActionEditor
            action={contactContent.cta ?? { text: '', action_type: 'email' }}
            onChange={(cta) => updateContent('cta', cta)}
            t={t}
          />
        </div>
      )}

      <div className="divider my-1" />

      {/* Published + Show in Nav + Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-5">
          <label className="flex items-center gap-2.5 cursor-pointer">
            <Toggle
              variant="primary"
              size="sm"
              checked={form.is_published}
              onChange={(e) => setForm((prev) => ({ ...prev, is_published: e.target.checked }))}
            />
            <span className="text-sm">{t('pages.fields.published')}</span>
          </label>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <Toggle
              variant="primary"
              size="sm"
              checked={form.show_in_nav}
              onChange={(e) => setForm((prev) => ({ ...prev, show_in_nav: e.target.checked }))}
            />
            <span className="text-sm">{t('pages.fields.show_in_nav')}</span>
          </label>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isSaving}>
            {tc('actions.cancel')}
          </Button>
          <Button type="submit" loading={isSaving}>
            {page ? t('pages.edit_page') : t('pages.add_page')}
          </Button>
        </div>
      </div>
    </form>
  )
}
